import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import moment from 'moment-timezone';
import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { SqlDWCopySource } from 'app/model/pipeline/copysource/sqldw.copysource';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { SqlTypeCopySource } from 'app/model/pipeline/copysource/sql-type.copysource';
import { Owned } from 'app/model/base/named';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { IModel } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlDWCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        sqlReaderQuery: '*',
        sqlReaderStoredProcedureName: '*',
        storedProcedureParameters: '*',
        queryTimeout: '*',
        isolationLevel: '*',
        partitionOption: '*',
        partitionSettings: {
            partitionColumnName: '*',
            partitionUpperBound: '*',
            partitionLowerBound: '*'
        }
    }
})
export class SqlDWCopySourceAdapter extends CopySourceAdapter<SqlDWCopySource, Model.SqlDWSource> {
    protected model(): IModel {
        return SqlDWCopySource.MODEL;
    }

    protected override async _toResource(model: SqlDWCopySource): Promise<Model.SqlDWSource> {
        const result: Model.SqlDWSource = this.getCommonCopySourceProperties(model, this.model().name);
        if (model.mode === 'query') {
            result.sqlReaderQuery = ValueAdapter.toResource(model, SqlTypeCopySource.READER_QUERY);
        } else if (model.mode === 'storedProcedure') {
            result.sqlReaderStoredProcedureName = ValueAdapter.toResource(
                model,
                SqlTypeCopySource.READER_STORED_PROCEDURE_NAME
            );
            if (model.readerStoredProcedureParameters != null && model.readerStoredProcedureParameters.length > 0) {
                const params: { [propertyName: string]: Model.StoredProcedureParameter } = {};
                model.readerStoredProcedureParameters.forEach((p) => {
                    params[p.name] = {
                        type: p.type,
                        value: ValueAdapter.toResource(p, ParameterConfig.VALUE)
                    };
                });
                result.storedProcedureParameters = params;
            } else {
                result.storedProcedureParameters = ValueAdapter.toResource(
                    model,
                    SqlTypeCopySource.READER_STORED_PROCEDURE_PARAMETERS
                );
            }
            // clear [] to keep the current behavior
            if (
                result.storedProcedureParameters &&
                Array.isArray(result.storedProcedureParameters) &&
                result.storedProcedureParameters.length === 0
            ) {
                result.storedProcedureParameters = undefined;
            }
        }

        try {
            result.queryTimeout =
                typeof model.queryTimeout === 'number'
                    ? moment.duration(model.queryTimeout, 'minutes').format('hh:mm:ss', {
                          trim: false
                      })
                    : ValueAdapter.toResource(model, SqlDWCopySource.QUERY_TIMEOUT);
        } catch {
            this._logError('Failed to convert queryTimeout into TimeSpan');
        }

        result.isolationLevel = ValueAdapter.toResource(model, SqlDWCopySource.ISOLATION_LEVEL);

        result.partitionOption = ValueAdapter.toResource(model, SqlDWCopySource.PARTITION_OPTION);
        if (
            SqlDWCopySource.PARTITION_OPTION.templateValue(model) != null ||
            model.partitionOption === partitionOptionConst.dyamicRange
        ) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(model, SqlDWCopySource.PARTITION_COLUMN_NAME),
                partitionUpperBound: ValueAdapter.toResource(model, SqlDWCopySource.PARTITION_UPPER_BOUND),
                partitionLowerBound: ValueAdapter.toResource(model, SqlDWCopySource.PARTITION_LOWER_BOUND)
            };
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.SqlDWSource,
        copyActivity: CopyActivity
    ): Promise<SqlDWCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            this.model(),
            resource,
            copyActivity
        ) as SqlDWCopySource;
        if (resource.sqlReaderQuery) {
            ValueAdapter.toModel(result, SqlTypeCopySource.READER_QUERY, resource.sqlReaderQuery);
            result.mode = 'query';
        } else if (resource.sqlReaderStoredProcedureName) {
            ValueAdapter.toModel(
                result,
                SqlTypeCopySource.READER_STORED_PROCEDURE_NAME,
                resource.sqlReaderStoredProcedureName
            );
            if (resource.storedProcedureParameters != null) {
                if (
                    typeof resource.storedProcedureParameters === 'object' &&
                    resource.storedProcedureParameters.type === 'Expression'
                ) {
                    ValueAdapter.toModel(
                        result,
                        SqlTypeCopySource.READER_STORED_PROCEDURE_PARAMETERS,
                        resource.storedProcedureParameters
                    );
                } else {
                    result.readerStoredProcedureParameters = Object.keys(resource.storedProcedureParameters).map(
                        (key) => {
                            const item = Owned.create<ParameterConfig>(ParameterConfig.MODEL, copyActivity);
                            item.name = key;
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            item.type = resource.storedProcedureParameters![key].type;
                            ValueAdapter.toModel(
                                item,
                                ParameterConfig.VALUE,
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                resource.storedProcedureParameters![key].value
                            );
                            return item;
                        }
                    );
                }
            }
            result.mode = 'storedProcedure';
        }

        try {
            if (resource.queryTimeout == null) {
                result.queryTimeout = undefined;
            } else {
                if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    result.queryTimeout = moment.duration(resource.queryTimeout).asMinutes();
                } else {
                    ValueAdapter.toModel(result, SqlDWCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert queryTimeout into number');
        }

        ValueAdapter.toModel(result, SqlDWCopySource.ISOLATION_LEVEL, resource.isolationLevel);

        if (resource.partitionOption != null) {
            ValueAdapter.toModel(result, SqlDWCopySource.PARTITION_OPTION, resource.partitionOption);
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(
                result,
                SqlDWCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                SqlDWCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                SqlDWCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
        }
        return result;
    }
}
